import {
  Shield,
  Sparkles,
  Layers,
  Car,
  Truck,
  Lightbulb,
  Wind,
  Droplets,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "ppf",
    title: "Premium Paint Protection Film",
    short: "Self-healing Veltik Clear PPF PRO 60 shields your paint from rock chips, road debris and minor scratches — crystal-clear gloss finish.",
    icon: Shield,
    tag: "Self-Healing PPF",
  },
  {
    slug: "ceramic-coating",
    title: "Ceramic Coating",
    short: "Long-lasting hydrophobic ceramic coatings for a deep showroom gloss.",
    icon: Sparkles,
    tag: "Hydrophobic Gloss",
  },
  {
    slug: "paint-correction",
    title: "Paint Correction",
    short: "One-step and multi-stage machine polishing reduces swirls, holograms and oxidation — restoring deep reflection.",
    icon: Layers,
    tag: "Mirror Finish",
  },
  {
    slug: "windshield-protection",
    title: "Windshield Protection Film",
    short: "Veltik SEC-H exterior windshield film — hydrophobic, optically clear protection against minor road debris damage. Not window tint.",
    icon: Shield,
    tag: "Hydrophobic Glass Film",
  },
  {
    slug: "interior-detailing",
    title: "Interior Detailing",
    short: "Regular interior refresh (~1.5–2 h) or Deep Interior Cleaning with extraction & steam (~3–4 h).",
    icon: Car,
    tag: "Two Levels",
  },
  {
    slug: "exterior-detailing",
    title: "Exterior Detailing",
    short: "Hand wash, wheels, tires, glass and dressing — add clay + iron paint decontamination when your paint needs it.",
    icon: Droplets,
    tag: "Decon Add-On Available",
  },
  {
    slug: "headlight-restoration",
    title: "Headlight Restoration",
    short: "Cloudy, yellowed lenses restored to optical clarity — safer driving and a sharper front-end.",
    icon: Lightbulb,
    tag: "Optical Clarity",
  },
  {
    slug: "odor-removal",
    title: "Odor Treatment",
    short: "We find and clean the odor source, then neutralize with enzyme or ozone treatment where appropriate — smoke, pets, spills, mildew.",
    icon: Wind,
    tag: "Source-First Treatment",
  },
  {
    slug: "bed-liner",
    title: "Bed Liner",
    short: "Professional spray-on bed liner — a tough textured coating with proper prep, masking and a controlled install.",
    icon: Truck,
    tag: "Spray-On",
  },
];

// Services not yet launched — shown as "Coming Soon" across the public site
export const COMING_SOON_SLUGS = [];

// Hidden services (owner, 2026-10-30): window tint is off the public site for
// now — not listed anywhere, and its page returns 404. Content stays in
// servicesContent.js; to re-launch, remove the slug here and re-add the
// SERVICES card above.
export const HIDDEN_SERVICE_SLUGS = ["window-tint"];

// Short service slug → full SEO page slug (keys of SERVICE_PAGES in servicesContent.js)
export const PAGE_SLUG_BY_SERVICE = {
  "window-tint": "window-tint-edmonton",
  "ppf": "ppf-edmonton",
  "windshield-protection": "windshield-protection-film-edmonton",
  "ceramic-coating": "ceramic-coating-edmonton",
  "paint-correction": "paint-correction-edmonton",
  "interior-detailing": "interior-detailing-edmonton",
  "exterior-detailing": "exterior-detailing-edmonton",
  "bed-liner": "bed-liner-edmonton",
  "headlight-restoration": "headlight-restoration-edmonton",
  "odor-removal": "odor-removal-edmonton",
};

// Dedicated service pages live at the top level: /ceramic-coating-edmonton etc.
export const servicePagePath = (slug) => `/${PAGE_SLUG_BY_SERVICE[slug] || slug}`;

export const WHY_CHOOSE = [
  { title: "Professional Installation", copy: "Careful, methodical installers who treat every vehicle like their own.", num: "01" },
  { title: "Premium Materials", copy: "Only tier-one films, coatings and chemicals — no shortcuts, ever.", num: "02" },
  { title: "Proven Products", copy: "Premium films and coatings selected for Edmonton's climate.", num: "03" },
  { title: "Aftercare Included", copy: "Clear aftercare guidance with every service — and we stand behind our workmanship.", num: "04" },
  { title: "Precision Installation", copy: "Computer-cut templates and hand-finished edges for a factory look.", num: "05" },
  { title: "Attention to Detail", copy: "Every panel, seam and edge inspected under focused lighting.", num: "06" },
];

export const BRANDS = ["Self-Healing PPF", "Ceramic Coating", "Paint Correction", "Spray-On Liner", "Detailing"];

export const REVIEWS = [
  {
    name: "Michael K.",
    role: "Porsche 911 Owner",
    rating: 5,
    body:
      "Best PPF install I've seen in Edmonton. Edges are wrapped clean, no lifting after 8 months. Worth every dollar.",
  },
  {
    name: "Sarah T.",
    role: "Tesla Model Y",
    rating: 5,
    body:
      "Ceramic tint transformed the cabin. Massive difference on those hot summer days. Team is friendly and precise.",
  },
  {
    name: "Daniel R.",
    role: "Audi RS5",
    rating: 5,
    body:
      "Paint correction + ceramic coating — my car looks better than the day I picked it up. Highly recommended.",
  },
  {
    name: "Emily W.",
    role: "BMW X5",
    rating: 5,
    body:
      "Interior detailing was next-level. Removed stains I thought were permanent. Booking again for spring.",
  },
  {
    name: "Jason M.",
    role: "Ford F-150",
    rating: 5,
    body:
      "Bed liner install is bulletproof. Clean masking, no overspray, tough finish. Real professionals.",
  },
  {
    name: "Olivia P.",
    role: "Mercedes GLE",
    rating: 5,
    body:
      "Booked headlight restoration on a whim — night driving is a whole new experience now. Fast and affordable.",
  },
];

export const FAQ = [
  {
    q: "How long does PPF last?",
    a: "Premium self-healing PPF is engineered for years of service. Lifespan and warranty coverage depend on the film line selected — we'll confirm the exact terms before installation. The self-healing top coat helps keep it looking new.",
  },
  {
    q: "Is ceramic coating better than wax?",
    a: "Yes. Wax typically provides short-term protection, while professional ceramic coatings can provide longer-term protection depending on the coating package, vehicle use and maintenance.",
  },
  {
    q: "How long does a full detail take?",
    a: "A full exterior + interior detail typically takes 1 day. Paint correction and coating packages can take 2–4 days depending on paint condition.",
  },
  {
    q: "Do you offer mobile service?",
    a: "Our coatings and PPF work is done in-shop under controlled lighting and dust-free conditions. Interior details can be arranged on-site.",
  },
  {
    q: "Do you offer warranties?",
    a: "Warranty coverage depends on the product and package you choose — we'll tell you exactly what applies to your service before you book.",
  },
];

export const CONTACT = {
  phone: "+1 (368) 380-8483",
  whatsapp: "https://wa.me/13683808483",
  email: "hello@wtddetailing.ca",
  address: "11629 — 145 Street NW, Edmonton, AB T5M 1V9",
  hours: [
    { day: "Mon – Fri", time: "9:00 AM — 6:00 PM" },
    { day: "Saturday", time: "10:00 AM — 4:00 PM" },
    { day: "Sunday", time: "By Appointment" },
  ],
  socials: [
    { name: "Instagram", handle: "@wtddetailing", url: "https://www.instagram.com/wtddetailing/" },
    { name: "WhatsApp", handle: "+1 (368) 380-8483", url: "https://wa.me/13683808483" },
  ],
};
